import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Building2,
  Plus,
  Factory,
  Pencil,
  Star,
  Trash2,
  Mail,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import AgencyFormDialog from "@/components/AgencyFormDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgency } from "@/context/AgencyContext";

export default function AgencyDetailPage() {
  const { id } = useParams();
  const [agency, setAgency] = useState(null);
  const [lines, setLines] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { refresh: refreshAgencyContext } = useAgency();

  const load = useCallback(async () => {
    const [{ data: a }, { data: ln }, { data: ms }] = await Promise.all([
      api.get(`/rep-agencies/${id}`),
      api.get(`/rep-agencies/${id}/lines`, { params: { include_inactive: true } }),
      api.get("/manufacturers"),
    ]);
    setAgency(a);
    setLines(ln);
    setAllManufacturers(ms);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = async () => {
    await load();
    await refreshAgencyContext();
  };

  const toggleLine = async (line) => {
    try {
      await api.patch(`/rep-agencies/${id}/lines/${line.id}`, {
        active: !line.active,
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const removeLine = async (line) => {
    if (!window.confirm(`Remove ${line.manufacturer?.name} from ${agency?.name}?`)) return;
    try {
      await api.delete(`/rep-agencies/${id}/lines/${line.id}`);
      toast.success("Line removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  if (!agency) {
    return <div className="byrd-progress" />;
  }

  const linkedManufacturerIds = new Set(lines.map((l) => l.manufacturer_id));
  const availableManufacturers = allManufacturers.filter((m) => !linkedManufacturerIds.has(m.id));

  return (
    <div className="space-y-6">
      <Link
        to="/agencies"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-900 font-mono-data"
        data-testid="back-to-agencies"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to agencies
      </Link>

      {/* Profile header */}
      <div className="bg-white border border-border rounded-sm p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 border border-border rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
            {agency.logo_url ? (
              <img src={agency.logo_url} alt={agency.name} className="w-full h-full object-contain p-1" />
            ) : (
              <Building2 className="w-7 h-7 text-slate-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-display font-bold tracking-tight text-slate-900">
                {agency.name}
              </h1>
              {agency.is_default && (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              )}
              <span
                className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-mono-data ${
                  agency.active !== false
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-rose-300 bg-rose-50 text-rose-700"
                }`}
              >
                {agency.active !== false ? "Active" : "Inactive"}
              </span>
            </div>
            {agency.website && (
              <a
                href={agency.website.startsWith("http") ? agency.website : `https://${agency.website}`}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 inline-block text-sm text-muted-foreground hover:text-slate-900"
              >
                {agency.website}
              </a>
            )}
            {agency.contact_info && (
              <div className="mt-2 text-xs text-slate-600 whitespace-pre-wrap">
                {agency.contact_info}
              </div>
            )}
            {agency.pricing_emails && agency.pricing_emails.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
                  Pricing
                </span>
                {agency.pricing_emails.map((e) => (
                  <span
                    key={e}
                    className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2 py-0.5 rounded-sm"
                  >
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            className="rounded-sm"
            data-testid="edit-agency-btn"
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        </div>
      </div>

      {/* Lines (manufacturers) */}
      <div>
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
              Portfolio
            </div>
            <h2 className="text-lg font-display font-bold tracking-tight text-slate-900">
              Manufacturers assigned to this agency
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Inactive lines are hidden from distributor search. Per-distributor
              line access is controlled in the approval flow.
            </p>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-line-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add manufacturer
          </Button>
        </div>

        {lines.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm bg-white p-10 text-center">
            <Factory className="w-8 h-8 mx-auto text-slate-400" />
            <div className="mt-2 text-sm text-muted-foreground">
              No manufacturers assigned yet.
            </div>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-border">
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
                  <th className="text-left px-4 py-2 font-medium">Manufacturer</th>
                  <th className="text-left px-4 py-2 font-medium w-32">Products</th>
                  <th className="text-left px-4 py-2 font-medium w-24">Active</th>
                  <th className="text-right px-4 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    onToggle={() => toggleLine(line)}
                    onRemove={() => removeLine(line)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AgencyFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        agency={agency}
        onSaved={onSaved}
      />

      <AddLineDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        availableManufacturers={availableManufacturers}
        agencyId={id}
        onSaved={load}
      />
    </div>
  );
}

function LineRow({ line, onToggle, onRemove }) {
  const m = line.manufacturer || {};

  return (
    <tr
      className="border-b border-border last:border-0 hover:bg-slate-50/50"
      data-testid={`line-row-${line.id}`}
    >
      <td className="px-4 py-3">
        <Link
          to={`/manufacturers/${m.id}`}
          className="font-medium text-slate-900 hover:text-amber-600"
        >
          {m.name}
        </Link>
        {m.website && (
          <div className="text-[11px] text-muted-foreground truncate">{m.website}</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs font-mono-data text-muted-foreground">
        {line.product_count} products
      </td>
      <td className="px-4 py-3">
        <Switch
          checked={line.active !== false}
          onCheckedChange={onToggle}
          data-testid={`line-toggle-${line.id}`}
        />
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-7 w-7 p-0 text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-sm"
          data-testid={`line-delete-${line.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function AddLineDialog({ open, onOpenChange, availableManufacturers, agencyId, onSaved }) {
  const [mode, setMode] = useState("existing");
  const [manufacturerId, setManufacturerId] = useState("");
  const [newName, setNewName] = useState("");
  const [newWebsite, setNewWebsite] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode(availableManufacturers.length > 0 ? "existing" : "new");
      setManufacturerId("");
      setNewName("");
      setNewWebsite("");
    }
  }, [open, availableManufacturers.length]);

  const submit = async (e) => {
    e.preventDefault();
    let payload;
    if (mode === "existing") {
      if (!manufacturerId) {
        toast.error("Pick a manufacturer");
        return;
      }
      payload = { manufacturer_id: manufacturerId, active: true };
    } else {
      if (!newName.trim()) {
        toast.error("Manufacturer name is required");
        return;
      }
      payload = {
        new_manufacturer: { name: newName.trim(), website: newWebsite.trim() },
        active: true,
      };
    }
    setSaving(true);
    try {
      await api.post(`/rep-agencies/${agencyId}/lines`, payload);
      toast.success("Manufacturer assigned");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Assignment failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="add-line-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Add manufacturer to agency</DialogTitle>
          <DialogDescription>
            Link an existing manufacturer or create a brand-new one inline.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={() => setMode("existing")}
              disabled={availableManufacturers.length === 0}
              className={`flex-1 px-3 py-1.5 rounded-sm border ${
                mode === "existing"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-border bg-white text-slate-700 hover:border-slate-400"
              } disabled:opacity-50`}
              data-testid="mode-existing-btn"
            >
              Existing manufacturer
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              className={`flex-1 px-3 py-1.5 rounded-sm border ${
                mode === "new"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-border bg-white text-slate-700 hover:border-slate-400"
              }`}
              data-testid="mode-new-btn"
            >
              New manufacturer
            </button>
          </div>

          {mode === "existing" ? (
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Manufacturer
              </label>
              {availableManufacturers.length === 0 ? (
                <p className="mt-1 text-xs text-muted-foreground italic">
                  All manufacturers are already assigned. Use "New manufacturer" instead.
                </p>
              ) : (
                <Select value={manufacturerId} onValueChange={setManufacturerId}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="manufacturer-select">
                    <SelectValue placeholder="Pick a manufacturer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableManufacturers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                  Name *
                </label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., TCP Lighting"
                  className="mt-1 rounded-sm"
                  data-testid="new-mfr-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                  Website
                </label>
                <Input
                  value={newWebsite}
                  onChange={(e) => setNewWebsite(e.target.value)}
                  placeholder="https://…"
                  className="mt-1 rounded-sm"
                  data-testid="new-mfr-website-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="add-line-save-btn"
            >
              {saving ? "Saving…" : "Add to agency"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
